import { Button, Icon } from '@perch-labs/shrike';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { ImageTypeUploader } from '~/components/image-uploader/image-type-uploader.component';
import { DeleteButton } from '~/components/image-uploader/image-uploader.style';
import { Inputs } from '~/components/inputs';
import { Separator } from '~/components/separator';
import { Description } from '~/components/typography';
import { LmiStatus } from '~/machines/review-form';
import { LDClient, LDFlags } from '~/providers';
import { LocalizeContext } from '~/providers/localize';
import { isEligibleForAIValidation } from '~/services/fetch-funnel-configuration/perch-project-details';
import {
  LmiDocumentValidationMessageKey,
  LmiQualificationStatus,
  LmiViolationSeverity,
  ValidateLmiDocumentResponse,
  getLmiDocumentValidationMessage,
  getPrimaryLmiViolation,
  validateLmiDocument,
} from '~/services/validate-lmi-document';
import { addFileProps, UploadedImage } from '~/utils/files';
import { trackError } from '~/utils/tracking';
import { LMIProofDocTypeSelect } from '../lmi-proof-doc-type-select';
import { handleUpload } from '../utils/handle-uploads';
import { StyledCrate, StyledDetailsContainer, StyledRadioGroup } from './document-details.style';
import { LMIProofDoc } from './lmi-proof-docs-upload.component';

interface DocumentFile {
  file: UploadedImage;
  proofType: string;
}

export interface Document {
  relationship: string;
  name: string;
  documentType: string;
  files: DocumentFile[];
  context?: any;
}

interface DocumentDetailsProps {
  allDocumentTypes: Record<string, string> | string[];
  pending: boolean;
  setShouldBeDisabled: (isValid: boolean) => void;
  setDocs: (docs: Document[]) => void;
  context: any;
  fullWidth?: boolean;
  onValidationPendingChange?: (pending: boolean) => void;
}

interface LmiValidationStatus {
  isBlocking: boolean;
  message: string;
  subtitle?: string;
  type: 'error' | 'pending' | 'success' | 'warning';
}

const getLocalizedSubtitle = (
  response: ValidateLmiDocumentResponse,
  language: string,
): string | undefined => {
  const violation = getPrimaryLmiViolation(response);
  const localizedSubtitle = language.startsWith('es')
    ? violation?.subtitle?.es
    : violation?.subtitle?.en;

  return localizedSubtitle || violation?.message || undefined;
};

const isBlockingValidationResponse = (response: ValidateLmiDocumentResponse): boolean => {
  if (
    response.messageKey === LmiDocumentValidationMessageKey.DocumentValid ||
    response.messageKey === LmiDocumentValidationMessageKey.ValidationUnavailable
  ) {
    return false;
  }

  return (
    response.qualificationStatus === LmiQualificationStatus.Failed ||
    response.violations.some(violation => violation.severity === LmiViolationSeverity.Error)
  );
};

const getValidationStatusType = (
  response: ValidateLmiDocumentResponse,
): LmiValidationStatus['type'] => {
  if (response.messageKey === LmiDocumentValidationMessageKey.DocumentValid) {
    return 'success';
  }

  return isBlockingValidationResponse(response) ? 'error' : 'warning';
};

const isDocumentComplete = (doc: Document): boolean =>
  Boolean(
    doc.documentType &&
      doc.name &&
      doc.relationship &&
      doc.files.length > 0 &&
      !doc.files.some(file => file.file.error),
  );

export const DocumentDetails = ({
  allDocumentTypes,
  pending,
  setShouldBeDisabled,
  setDocs,
  context,
  fullWidth = false,
  onValidationPendingChange,
}: DocumentDetailsProps) => {
  const initialDocument = {
    name: '',
    relationship: '',
    documentType: '',
    files: [],
  };

  const [documents, setDocuments] = useState<Document[]>([initialDocument]);

  const [failure, setFailure] = useState(false);
  const [validationStatuses, setValidationStatuses] = useState<Record<string, LmiValidationStatus>>(
    {},
  );

  const isMinnesota = context.funnelPacket?.utility.state.abbreviation === 'MN';
  const language = useContext(LocalizeContext);
  const shouldValidateLmiDocument =
    Boolean(LDClient.variation(LDFlags.enableLmiDocumentValidation)) &&
    isEligibleForAIValidation(context);

  const interactionPending = pending;

  const handleDocumentChange = (
    field: keyof typeof initialDocument,
    index: number,
    value: DocumentFile[] | string,
  ) => {
    setDocuments(prevState => {
      const newDocuments = [...prevState];
      newDocuments[index] = { ...newDocuments[index], [field]: value };
      return newDocuments;
    });
  };

  const relationshipOptions = useCallback((index, documentType) => {
    const relationships = {
      [`self-${index}-${documentType}`]: 'Myself',
      [`other-${index}-${documentType}`]: 'Another member of my household',
    };
    return Object.keys(relationships).map(relationship => {
      return {
        children: <p key={relationship}>{relationships[relationship]}</p>,
        value: relationship,
      };
    });
  }, []);

  const addDocFields = () => {
    const newDocs = [...documents, initialDocument];
    setDocuments(newDocs);
  };

  const hasErrorDoc = documents.some(document => document.files.some(file => file.file.error));
  const hasPendingValidationStatus = useMemo(
    () =>
      documents.some(document =>
        document.files.some(
          ({ file }) => !file.error && validationStatuses[file.id]?.type === 'pending',
        ),
      ),
    [documents, validationStatuses],
  );
  const hasBlockingValidationStatus = useMemo(
    () =>
      documents.some(document => {
        const uploadedFiles = document.files.filter(({ file }) => !file.error);
        if (uploadedFiles.length === 0) return false;

        const hasBlockingFile = uploadedFiles.some(
          ({ file }) => validationStatuses[file.id]?.isBlocking === true,
        );
        const hasNonBlockingFile = uploadedFiles.some(
          ({ file }) => validationStatuses[file.id]?.isBlocking === false,
        );

        return hasBlockingFile && !hasNonBlockingFile;
      }),
    [documents, validationStatuses],
  );
  const isFormValid = useMemo(() => {
    // Check if we have at least one document with files
    const hasDocumentsWithFiles = documents.some(document => document.files.length > 0);

    // Check if all filled documents are complete
    const allFilledDocumentsValid = documents
      .filter(doc => doc.documentType || doc.name || doc.relationship || doc.files.length > 0) // Documents that have been started
      .every(isDocumentComplete); // Must be complete

    return (
      hasDocumentsWithFiles &&
      allFilledDocumentsValid &&
      !failure &&
      !hasErrorDoc &&
      !hasPendingValidationStatus &&
      !hasBlockingValidationStatus
    );
  }, [documents, failure, hasBlockingValidationStatus, hasErrorDoc, hasPendingValidationStatus]);

  const validateUploadedDocument = useCallback(
    async (document: Document, proofType: string, file: UploadedImage) => {
      if (!shouldValidateLmiDocument) return;

      onValidationPendingChange?.(true);
      setValidationStatuses(prev => ({
        ...prev,
        [file.id]: {
          isBlocking: false,
          message: '',
          type: 'pending',
        },
      }));

      try {
        const response = await validateLmiDocument({
          accountId: context.funnelPacket?.account?.id,
          documentType: proofType,
          funnelId: context.funnelId,
          nameOnDocument: document.name,
          userId: context.funnelPacket?.user?.id,
          page: { file },
          qualifyingProgram: document.documentType,
          relationship: document.relationship.split('-')[0] || document.relationship,
          utilityAccountId: context.funnelPacket?.utilityAccount?.id,
          utilityId: context.funnelPacket?.utility.id,
          validationRuleset: isMinnesota ? 'mn-state-lmi' : undefined,
        });

        setValidationStatuses(prev => ({
          ...prev,
          [file.id]: {
            isBlocking: isBlockingValidationResponse(response),
            message: getLmiDocumentValidationMessage(response.messageKey),
            subtitle: getLocalizedSubtitle(response, language),
            type: getValidationStatusType(response),
          },
        }));
      } catch (error) {
        trackError(new Error('Error validating LMI proof document'), {
          extras: {
            documentType: proofType,
            error,
          },
        });
        setValidationStatuses(prev => ({
          ...prev,
          [file.id]: {
            isBlocking: false,
            message: getLmiDocumentValidationMessage(
              LmiDocumentValidationMessageKey.ValidationUnavailable,
            ),
            type: 'warning',
          },
        }));
      } finally {
        onValidationPendingChange?.(false);
      }
    },
    [
      context.funnelId,
      context.funnelPacket?.account?.id,
      context.funnelPacket?.user?.id,
      context.funnelPacket?.utility.id,
      isMinnesota,
      language,
      onValidationPendingChange,
      shouldValidateLmiDocument,
    ],
  );

  useEffect(() => {
    // Keep submit disabled while any started row is incomplete, but never wipe
    // already-complete docs from the parent — an incomplete "add another" row
    // previously cleared valid proof docs from the submit payload.
    setShouldBeDisabled(isFormValid);
    setDocs(documents.filter(isDocumentComplete));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents, isFormValid]);

  // Reusable function for rendering document input fields
  const renderDocumentFields = (document: Document, index: number) => (
    <>
      <fieldset disabled={interactionPending} style={{ border: 0, margin: 0, padding: 0 }}>
        <Inputs.NameOnDocument
          margin="16px 0 0 0 "
          required
          onChange={value => handleDocumentChange('name', index, value)}
        />
        <StyledRadioGroup
          legend="Relationship"
          margin="16px 0 0 0"
          name={`lmi-relationship-${index}-${document.documentType}`}
          options={relationshipOptions(index, document.documentType)}
          isChecked={(_, value) => value === document.relationship}
          onChange={(selection: LmiStatus) => {
            handleDocumentChange('relationship', index, selection);
          }}
          width="initial"
        />
      </fieldset>
      <Separator padding="8px 0" />
      <Description mb={0} fontWeight={700} fontSize="16px" padding="0">
        Eligibility documents
      </Description>
      <Description mb={0} fontSize="14px" padding="16px 0">
        Your document must show{' '}
        <strong>the program name, the participant name, and issue or expiration date.</strong> If
        any of this info is missing, upload a second document that includes it. Make sure the same
        name or ID appears on both documents.
      </Description>
      {isMinnesota ? (
        <Description mb={0} fontSize="14px" padding="0 0 16px 0">
          We only need certain information to process your application. Please remove or black out
          any sensitive information (such as Social Security numbers or bank account numbers) before
          uploading documents.
        </Description>
      ) : null}
      <Separator padding="8px 0" />
      <ImageTypeUploader
        key={`image-uploader-${index}`}
        context={context}
        acceptFileTypes={{
          'image/jpg': ['.jpg', '.jpeg', '.png', '.heic'],
          'application/pdf': ['.pdf'],
        }}
        copy={{ documentType: 'document' }}
        uploadedImages={document.files}
        maxImages={0}
        pageType="multiple"
        pending={
          interactionPending ||
          (shouldValidateLmiDocument &&
            (!document.name || !document.relationship || !document.documentType))
        }
        validationStatuses={validationStatuses}
        onUploadSuccess={async (proofType: string, file: File, base64Image: string) => {
          try {
            setFailure(false);
            const newDoc: LMIProofDoc = await handleUpload({
              file,
              base64Image,
              documentType: document.documentType,
            });
            const uploadedFile = Object.assign(newDoc.image, { uploadId: Date.now() });
            if (shouldValidateLmiDocument) {
              setValidationStatuses(prev => ({
                ...prev,
                [uploadedFile.id]: {
                  isBlocking: false,
                  message: '',
                  type: 'pending',
                },
              }));
            }
            handleDocumentChange('files', index, [
              ...document.files,
              {
                proofType,
                file: uploadedFile,
              },
            ]);
            await validateUploadedDocument(document, proofType, uploadedFile);
          } catch (error) {
            trackError(new Error('Error uploading proof document'), {
              extras: {
                documentType: document.documentType,
                error,
              },
            });
            setFailure(true);
          }
        }}
        onUploadFailure={(proofType: string, fileRejection: FileRejection) => {
          setFailure(true);
          handleDocumentChange('files', index, [
            ...document.files,
            { proofType, file: addFileProps(fileRejection) },
          ]);
        }}
        onDelete={function (id: string): void {
          setFailure(false);
          setValidationStatuses(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
          handleDocumentChange('files', index, [
            ...document.files.filter(file => file.file.id !== id),
          ]);
        }}
      />
    </>
  );

  return (
    <>
      {documents.map((document, index) => {
        return (
          <StyledCrate key={document.documentType} $fullWidth={fullWidth}>
            {index > 0 && (
              <DeleteButton
                disabled={interactionPending}
                onClick={() => {
                  if (interactionPending) return;
                  setDocuments(
                    documents
                      .filter((_, i) => i !== index)
                      .map((document, index) => {
                        const newRelationship = document.relationship.replace(
                          /\d+/,
                          index.toString(),
                        );
                        return { ...document, relationship: newRelationship };
                      }),
                  );
                }}
                data-testid="delete-upload-button"
              >
                <Icon icon="UIDelete" title="delete-image" />
              </DeleteButton>
            )}
            <StyledDetailsContainer>
              <LMIProofDocTypeSelect
                label="Qualifying program"
                allDocumentTypes={allDocumentTypes}
                addedDocumentTypes={documents.map(doc => doc.documentType)}
                selectedDocumentType={document.documentType}
                onChange={newType => handleDocumentChange('documentType', index, newType)}
                disabled={
                  interactionPending || (document.files.length > 0 && document.documentType !== '')
                }
              />

              {/* Progressive rendering for MN, immediate rendering for other states */}
              {isMinnesota
                ? !!document.documentType && renderDocumentFields(document, index)
                : renderDocumentFields(document, index)}
            </StyledDetailsContainer>
          </StyledCrate>
        );
      })}
      {documents.length > 0 && documents.some(doc => doc.files.length > 0) && (
        <Button
          disabled={interactionPending || failure || !isFormValid}
          onClick={addDocFields}
          fullWidth
        >
          Add additional program {documents.length >= 1 ? '(optional)' : ''}
        </Button>
      )}
    </>
  );
};
